/* =====================================================================
 * DMMessageInput — DM 메시지 입력창
 *
 * 에이전트 채팅 MessageInput 의 InputCard 패턴과 동일한 스타일 적용
 * + 버튼 → 드롭다운 메뉴 (로컬 업로드 / 클라우드 업로드)
 * ===================================================================== */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ArrowUp, Plus } from 'lucide-react';
import type { DMAgentType } from '@auw/workspace-utils/types/dm';
import { getAgentInfo } from '@auw/workspace-utils/types/dm';
import { useChatStore } from '@auw/stores';
import { useFeature } from '@auw/feature-flags';

interface Props {
  agentType: DMAgentType;
  onSend: (content: string) => void;
  disabled?: boolean;
  transparent?: boolean;
}

const DMMessageInput: React.FC<Props> = ({
  agentType,
  onSend,
  disabled,
  transparent = false,
}) => {
  const { t } = useTranslation('common');
  /* P11-5h: agent-DM "+" stub never opens a real upload dialog, but tenants
   * with `mode.message.fileAttach` disabled should not even hint at file
   * sharing — hide the button entirely. */
  const fileAttachEnabled = useFeature('mode.message.fileAttach');
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const shouldRestoreFocusRef = useRef(false);
  const addToast = useChatStore((s) => s.addToast);
  const agent = getAgentInfo(agentType);

  const restoreInputFocus = useCallback(() => {
    const target = inputRef.current;
    if (!target) return;

    const applyFocus = () => {
      if (!target.isConnected) return false;
      if (document.activeElement !== target) {
        target.focus({ preventScroll: true });
      }
      const caret = target.value.length;
      try {
        target.setSelectionRange(caret, caret);
      } catch {
        // noop
      }
      return document.activeElement === target;
    };

    if (applyFocus()) return;
    window.requestAnimationFrame(() => {
      if (applyFocus()) return;
      window.setTimeout(() => {
        applyFocus();
      }, 24);
    });
  }, []);

  /* auto-resize — 채팅 MessageInput 과 동일 패턴 */
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = '0';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [value]);

  useLayoutEffect(() => {
    if (!shouldRestoreFocusRef.current) return;
    shouldRestoreFocusRef.current = false;
    restoreInputFocus();
  }, [restoreInputFocus, value, disabled]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    shouldRestoreFocusRef.current = true;
    restoreInputFocus();
  }, [value, disabled, onSend, restoreInputFocus]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      /* IME 조합 중 Enter 무시 (한글 중복 방지) */
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handlePlusClick = useCallback(() => {
    if (disabled) return;
    addToast({
      type: 'info',
      title: t('dm.preparing'),
      description: t('dm.fileShareComingSoon'),
    });
  }, [addToast, disabled]);

  const canSend = value.trim().length > 0 && !disabled;

  return (
    <OuterWrapper>
      <Wrapper>
        <InputCard $focus={focused} $transparent={transparent}>
          <TextAreaWrap>
            <TextArea
              ref={inputRef}
              rows={1}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={handleKeyDown}
              placeholder={t('dm.messageToAgent', { name: agent.name })}
            />
          </TextAreaWrap>

          <InputRow>
            {/* + 메뉴 */}
            {fileAttachEnabled && (
              <IconBtn onClick={handlePlusClick} disabled={disabled} title={t('dm.fileSharePreparing')}>
                <Plus size={18} />
              </IconBtn>
            )}
            <SendBtn
              $canSend={canSend}
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSend}
              disabled={!canSend}
              title={t('dm.send')}
            >
              <ArrowUp size={18} />
            </SendBtn>
          </InputRow>
        </InputCard>
      </Wrapper>
    </OuterWrapper>
  );
};

export default DMMessageInput;

/* ---- Styled ---- */

const OuterWrapper = styled.div`
  position: relative;
`;

/** 채팅 MessageInput.Wrapper 와 동일 패턴 */
const Wrapper = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.input.wrapperPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.inputMobile.wrapperPad};
  }
`;

/** 채팅 MessageInput.InputCard 와 동일 스타일 */
const InputCard = styled.div<{ $focus: boolean; $transparent: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${({ theme, $transparent }) =>
    $transparent
      ? 'transparent'
      : theme.mode === 'dark' ? 'rgba(40,40,42,0.85)' : 'rgba(255,255,255,0.85)'};
  backdrop-filter: ${({ $transparent }) => ($transparent ? 'none' : 'blur(16px) saturate(180%)')};
  -webkit-backdrop-filter: ${({ $transparent }) => ($transparent ? 'none' : 'blur(16px) saturate(180%)')};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  border: 0.5px solid ${({ theme, $transparent }) =>
    $transparent
      ? theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'
      : theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  box-shadow: ${({ theme, $transparent }) =>
    $transparent
      ? 'none'
      : theme.mode === 'dark'
        ? '0 2px 12px rgba(0,0,0,0.25)'
        : '0 2px 12px rgba(0,0,0,0.05)'};
  transition: all 0.2s ease;

  ${({ $focus, theme }) =>
    $focus &&
    css`
      border-color: ${theme.mode === 'dark'
        ? 'rgba(255,255,255,0.14)'
        : 'rgba(0,0,0,0.1)'};
      box-shadow: ${theme.mode === 'dark'
        ? '0 4px 20px rgba(0,0,0,0.35), 0 0 0 0.5px rgba(255,255,255,0.08)'
        : '0 4px 20px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(0,0,0,0.04)'};
    `}
`;

/** 채팅 MessageInput.InputRow 와 동일 */
const InputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.input.rowPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.inputMobile.rowPad};
  }
`;

const TextAreaWrap = styled.div`
  width: 100%;
  padding: 12px 16px 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 10px 12px 0;
  }
`;

/** 채팅 MessageInput.TextArea 와 동일 스타일 */
const TextArea = styled.textarea`
  width: 100%;
  display: block;
  box-sizing: border-box;
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.input.fontSize};
  line-height: ${({ theme }) => theme.input.lineHeight};
  padding: ${({ theme }) => theme.input.textPad};
  padding-left: 8px;
  padding-right: 8px;
  min-height: 24px;
  max-height: ${({ theme }) => theme.sizes.inputMaxHeight};
  overflow-y: auto;

  /* 스크롤바: 내용이 넘칠 때만 얇게 표시 */
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 2px;
  }
  &:hover::-webkit-scrollbar-thumb,
  &:focus::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  }
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  &:hover,
  &:focus {
    scrollbar-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'} transparent;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.inputMobile.fontSize};
    line-height: ${({ theme }) => theme.inputMobile.lineHeight};
    padding: ${({ theme }) => theme.inputMobile.textPad};
    padding-left: 6px;
    padding-right: 6px;
  }
`;

/** 채팅 MessageInput.IconBtn 과 동일 스타일 — + 버튼 */
const IconBtn = styled.button`
  width: ${({ theme }) => theme.input.iconBtnSize};
  height: ${({ theme }) => theme.input.iconBtnSize};
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.inputMobile.iconBtnSize};
    height: ${({ theme }) => theme.inputMobile.iconBtnSize};
  }
`;

/** 채팅 MessageInput.IconBtn($primary) 와 동일 스타일 — 전송 버튼 */
const SendBtn = styled.button<{ $canSend: boolean }>`
  width: ${({ theme }) => theme.input.iconBtnSize};
  height: ${({ theme }) => theme.input.iconBtnSize};
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ theme }) => theme.colors.textPrimary};
  color: ${({ theme }) => theme.colors.bgPrimary};

  ${({ $canSend }) =>
    $canSend
      ? css`
          &:hover {
            opacity: 0.85;
          }
        `
      : css`
          opacity: 0.3;
          cursor: not-allowed;
        `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.inputMobile.iconBtnSize};
    height: ${({ theme }) => theme.inputMobile.iconBtnSize};
  }
`;

